import * as XLSX from 'xlsx'
import { supabase } from '../lib/supabase'
import riskAppetiteData from '../data/riskAppetite.json'
import businessScopingData from '../data/businessScoping.json'
import technologyScopingData from '../data/technologyScoping.json'
import maturityAssessmentData from '../data/maturityAssessment.json'

// Exports a multi-sheet XLSX containing all Discover-phase data for a single
// assessment. Intended for manual downstream use (e.g. drafting reports in
// another tool). Does NOT include follow-up flags or follow-up notes.
//
// Sheets:
//   1. Client Profile
//   2. Risk Appetite
//   3. Business Scoping
//   4. Technology Scoping
//   5. Maturity Assessment
//   6. Critical Systems
//   7. Security Tooling
//   8. Threat Context (TIDA)
//   9. Impact Data (TIDA)
//  10. Client Intelligence
//  11. Computed Scores

export async function exportDiscoveryDataXlsx({ client, assessment, scores }) {
  const wb = XLSX.utils.book_new()

  // Pull DB-backed data in parallel
  const [criticalSystemsResult, techStackResult, enrichmentResult] = await Promise.all([
    supabase.from('client_critical_systems').select('*').eq('client_id', client.id),
    supabase.from('client_technology_stack').select('*').eq('client_id', client.id),
    supabase.from('client_enrichment').select('*').eq('client_id', client.id).maybeSingle()
  ])

  addSheet(wb, 'Client Profile', buildClientProfileRows(client))
  addSheet(wb, 'Risk Appetite', buildRiskAppetiteRows(assessment?.responses?.riskAppetite || {}))
  addSheet(wb, 'Business Scoping', buildScopingRows(businessScopingData, assessment?.responses?.businessScoping || {}))
  addSheet(wb, 'Technology Scoping', buildScopingRows(technologyScopingData, assessment?.responses?.technologyScoping || {}))
  addSheet(wb, 'Maturity Assessment', buildMaturityRows(assessment?.responses?.maturityAssessment || {}))
  addSheet(wb, 'Critical Systems', buildCriticalSystemsRows(criticalSystemsResult.data || []))
  addSheet(wb, 'Security Tooling', buildTechStackRows(techStackResult.data || []))
  addSheet(wb, 'Threat Context (TIDA)', buildThreatContextRows(assessment))
  addSheet(wb, 'Impact Data (TIDA)', buildImpactDataRows(assessment))
  addSheet(wb, 'Client Intelligence', buildEnrichmentRows(enrichmentResult.data))
  addSheet(wb, 'Computed Scores', buildScoresRows(scores))

  const safeName = (client?.name || 'client').replace(/[^a-z0-9]+/gi, '-').toLowerCase()
  const dateStr = new Date().toISOString().slice(0, 10)
  XLSX.writeFile(wb, `${safeName}-discovery-data-${dateStr}.xlsx`)
}

// ============================================================
// Sheet builders
// ============================================================

function buildClientProfileRows(client) {
  const c = client || {}
  const contact = (name, email) => name || email ? `${name || ''}${email ? ` <${email}>` : ''}`.trim() : ''
  return [
    ['Field', 'Value'],
    ['Name', c.name || ''],
    ['Industry', c.industry || ''],
    ['Sub-Industry', c.industrySubCategory || c.industry_sub_category || ''],
    ['Status', c.status || ''],
    ['Staffing Size', c.staffingSize || c.staffing_size || ''],
    ['Annual Revenue', c.annualRevenue || c.annual_revenue || ''],
    ['IT Team Size', c.itTeamSize || c.it_team_size || ''],
    ['Geographical Presence', c.geographicalPresence || c.geographical_presence || ''],
    ['Domain', c.domain || ''],
    ['Primary Contact', contact(c.contactName, c.contactEmail)],
    ['Secondary Contact', contact(c.secondaryContactName, c.secondaryContactEmail)],
    ['Regulatory Frameworks', (c.regulatoryFrameworks || []).join(', ')],
    ['Enrichment Context', c.enrichmentContext || ''],
    ['Notes', c.notes || ''],
    ['Created At', c.createdAt || c.created_at || '']
  ]
}

function buildRiskAppetiteRows(responses) {
  const rows = [['Question ID', 'Domain', 'Question', 'Response Value', 'Response Label']]
  ;(riskAppetiteData.questions || []).forEach(q => {
    const value = responses[q.id]
    const selectedOption = (q.options || []).find(o => o.value === value)
    rows.push([
      q.id,
      q.domain || '',
      q.question,
      value ?? '',
      selectedOption?.label || ''
    ])
  })
  return rows
}

function buildScopingRows(schema, responses) {
  const rows = [['Question ID', 'Section', 'Category', 'Question', 'Response', 'Weight']]
  ;(schema.sections || []).forEach(section => {
    ;(section.questions || []).forEach(q => {
      const response = responses[q.id]
      const value = response && typeof response === 'object' ? response.value : response
      const weight = response && typeof response === 'object' ? response.weight : ''
      rows.push([
        q.id,
        section.name || section.id || '',
        q.category || '',
        q.question,
        formatScopingValue(value),
        weight ?? ''
      ])
    })
  })
  return rows
}

function formatScopingValue(value) {
  if (value == null) return ''
  if (Array.isArray(value)) return value.join('; ')
  if (typeof value === 'object') return JSON.stringify(value)
  return String(value)
}

function buildMaturityRows(responses) {
  const rows = [[
    'Question ID',
    'Domain #',
    'Category',
    'Question',
    'Response',
    'Base Weight',
    'Framework Mapping',
    'Context Questions',
    'Rubric (matched)'
  ]]
  ;(maturityAssessmentData.sections || []).forEach(section => {
    ;(section.questions || []).forEach(q => {
      const response = responses[q.id]
      const rubricText = response && q.rubric?.[response] ? q.rubric[response] : ''
      rows.push([
        q.id,
        q.domain ?? '',
        q.category || section.name || '',
        q.question,
        response || '',
        q.baseWeight ?? '',
        q.frameworkMapping || '',
        q.contextQuestions || '',
        rubricText
      ])
    })
  })
  return rows
}

function buildCriticalSystemsRows(systems) {
  const rows = [[
    'Name', 'Vendor', 'Business Function', 'Criticality Tier',
    'Data Sensitivity', 'Business Impact', 'Owner',
    'RTO (hours)', 'RPO (hours)', 'MTD (hours)',
    'Hourly Cost Impact ($)', 'Revenue Dependency (%)',
    'AI Estimated', 'AI Reasoning', 'Description', 'Notes'
  ]]
  ;(systems || []).forEach(s => {
    rows.push([
      s.name || '',
      s.vendor || '',
      s.business_function || '',
      s.criticality_tier || '',
      (s.data_sensitivity || []).join('; '),
      s.business_impact || '',
      s.owner || '',
      s.rto_hours ?? '',
      s.rpo_hours ?? '',
      s.mtd_hours ?? '',
      s.hourly_cost_impact ?? '',
      s.revenue_dependency_pct ?? '',
      s.ai_estimated ? 'Yes' : 'No',
      s.ai_reasoning || '',
      s.description || '',
      s.notes || ''
    ])
  })
  return rows
}

function buildTechStackRows(tools) {
  const rows = [[
    'Name', 'Vendor', 'Categories', 'Asset Classes (CDM)',
    'Operational Functions (CDM)', 'Description', 'Notes'
  ]]
  ;(tools || []).forEach(t => {
    const cats = Array.isArray(t.categories) && t.categories.length > 0
      ? t.categories
      : (t.category ? [t.category] : [])
    rows.push([
      t.name || '',
      t.vendor || '',
      cats.join('; '),
      (t.asset_classes || []).join('; '),
      (t.operational_functions || []).join('; '),
      t.description || '',
      t.notes || ''
    ])
  })
  return rows
}

function buildThreatContextRows(assessment) {
  const tc = assessment?.responses?.notes?.threatContext || assessment?.responses?.threatContext || {}
  const rows = [['Field', 'Value']]
  rows.push(['Profile Key', tc.profileKey || ''])

  rows.push([])
  rows.push(['Threat Actor Weights'])
  rows.push(['Actor', 'Weight'])
  Object.entries(tc.actorWeights || {}).forEach(([k, v]) => rows.push([k, v]))

  rows.push([])
  rows.push(['Attack Vector Weights'])
  rows.push(['Vector', 'Weight'])
  Object.entries(tc.vectorWeights || {}).forEach(([k, v]) => rows.push([k, v]))

  rows.push([])
  rows.push(['Target Asset Weights'])
  rows.push(['Asset', 'Weight'])
  Object.entries(tc.assetWeights || {}).forEach(([k, v]) => rows.push([k, v]))

  return rows
}

const IMPACT_DATA_FIELDS = [
  'estimated_annual_revenue',
  'employee_count',
  'data_record_count',
  'client_retention',
  'downtime_cost_per_hour',
  'recovery_time_objective',
  'ruin_time_horizon',
  'has_cyber_insurance',
  'insurance_carrier',
  'insurance_carrier_other',
  'insurance_coverage_limit',
  'cyber_reserves'
]

function buildImpactDataRows(assessment) {
  const imp = assessment?.responses?.notes?.impactData || assessment?.responses?.impactData || {}
  const rows = [['Field', 'Value']]
  IMPACT_DATA_FIELDS.forEach(key => {
    const v = imp[key]
    rows.push([prettyKey(key), v == null ? '' : (Array.isArray(v) || typeof v === 'object' ? JSON.stringify(v) : v)])
  })
  return rows
}

function buildEnrichmentRows(enrichment) {
  const e = enrichment?.enrichment || {}
  const rows = [['Section', 'Field', 'Value']]

  const push = (section, field, value) => {
    const cell = value == null ? '' : (Array.isArray(value) ? value.join('; ') : typeof value === 'object' ? JSON.stringify(value) : String(value))
    rows.push([section, field, cell])
  }

  const companyKeys = ['industry', 'subIndustry', 'businessModel', 'estimatedSize', 'estimatedRevenue', 'description', 'headquarters', 'yearsInOperation']
  companyKeys.forEach(k => push('Company Profile', prettyKey(k), e.companyProfile?.[k]))

  const leadership = e.companyProfile?.leadership || []
  if (leadership.length === 0) {
    push('Leadership', '(placeholder)', '')
  } else {
    leadership.forEach(l => push('Leadership', l.title || 'Leader', `${l.name || ''}${l.source ? ` (${l.source})` : ''}`))
  }

  const regKeys = ['applicableFrameworks', 'dataTypes', 'publiclyTraded', 'governmentContractor', 'processesPayments', 'handlesHealthData', 'operatesInternationally']
  regKeys.forEach(k => push('Regulatory Profile', prettyKey(k), e.regulatoryProfile?.[k]))

  const techKeys = ['emailPlatform', 'cloudPlatform', 'identityPlatform', 'dnsProvider', 'hostingProvider', 'websiteTechnology', 'detectedServices', 'otherTools']
  techKeys.forEach(k => push('Technology Profile', prettyKey(k), e.technologyProfile?.[k]))

  const postureKeys = ['overallGrade', 'overallGradeRationale', 'emailSecurity', 'certificateSecurity', 'dnsSecurity', 'attackSurface', 'initialObservations', 'quickWins', 'suggestedCriticalSystems']
  postureKeys.forEach(k => push('Security Posture', prettyKey(k), e.securityPosture?.[k]))

  const engagementKeys = ['threatProfileRecommendation', 'maturityEstimate', 'maturityRationale', 'keyRisks', 'suggestedFocusAreas', 'conversationStarters']
  engagementKeys.forEach(k => push('Engagement Context', prettyKey(k), e.engagementContext?.[k]))

  push('Confidence', 'Overall', e.confidence?.overall)
  push('Confidence', 'Notes', e.confidence?.notes)

  push('DNS', 'Email Provider', enrichment?.dns?.emailProvider)
  push('DNS', 'SPF', enrichment?.dns?.hasSPF == null ? '' : (enrichment.dns.hasSPF ? 'Yes' : 'No'))
  push('DNS', 'DMARC', enrichment?.dns?.dmarcPolicy || (enrichment?.dns?.hasDMARC == null ? '' : (enrichment.dns.hasDMARC ? 'Yes' : 'No')))
  push('DNS', 'DKIM', enrichment?.dns?.hasDKIM == null ? '' : (enrichment.dns.hasDKIM ? 'Yes' : 'No'))

  push('Meta', 'Generated At', enrichment?.generated_at)
  return rows
}

function buildScoresRows(scores) {
  const rows = [['Metric', 'Value']]
  rows.push(['Overall Score', scores?.overall?.score ?? ''])
  rows.push(['Maturity Level', scores?.overall?.maturityLevel?.label || ''])
  rows.push(['Maturity Level #', scores?.overall?.maturityLevel?.level ?? ''])

  rows.push([])
  rows.push(['Category Scores'])
  rows.push(['Category', 'Score', 'Maturity Level'])
  const cats = scores?.maturity?.categoryScores || {}
  Object.entries(cats).forEach(([id, c]) => {
    rows.push([c.name || id, Math.round(c.score || 0), c.maturityLevel?.label || ''])
  })

  rows.push([])
  rows.push(['Risk Appetite (by domain)'])
  rows.push(['Domain', 'Score', 'Label'])
  const domains = scores?.riskAppetite?.domains || {}
  Object.entries(domains).forEach(([domain, d]) => {
    rows.push([domain, d.score ?? '', d.label || ''])
  })

  rows.push([])
  rows.push(['Risk Appetite Average', scores?.riskAppetite?.averageAppetite ?? '', scores?.riskAppetite?.averageLabel || ''])

  return rows
}

// ============================================================
// Utilities
// ============================================================

function addSheet(wb, name, rows) {
  const ws = XLSX.utils.aoa_to_sheet(rows)
  // Auto-size columns based on content length, capped at 60
  const colWidths = []
  rows.forEach(row => {
    row.forEach((cell, i) => {
      const len = cell == null ? 0 : String(cell).length
      if (!colWidths[i] || len > colWidths[i].wch) colWidths[i] = { wch: Math.min(60, Math.max(10, len + 2)) }
    })
  })
  ws['!cols'] = colWidths
  // XLSX sheet names max 31 chars
  XLSX.utils.book_append_sheet(wb, ws, name.slice(0, 31))
}

function prettyKey(key) {
  return key
    .replace(/([A-Z])/g, ' $1')
    .replace(/_/g, ' ')
    .replace(/^./, c => c.toUpperCase())
    .trim()
}
